/**
 * Plan utility functions and constants
 * Centralized plan name mappings and product lookup logic
 */

/**
 * Maps URL plan parameters to display names
 */
export const PLAN_DISPLAY_NAMES = {
  'guided': 'Guided',
  'aris_plus': 'Aris Plus',
  // Backward-compat: older slug for the same plan
  'aligned_plus': 'Aris Plus'
};

/**
 * Maps URL plan parameters to product name patterns for searching (matched
 * against the product `name` field returned by the products API)
 */
export const PLAN_NAME_PATTERNS = {
  'guided': 'Guided',
  'aris_plus': 'Aris Plus',
  'aligned_plus': 'Aris Plus'
};

/**
 * Maps the URL plan slug to the backend product `plan_type`.
 * The marketing URLs use the slug `aris_plus`, but the products API still
 * returns this plan with plan_type `aligned_plus`.
 */
export const PLAN_TYPE_MAP = {
  'guided': 'guided',
  'aris_plus': 'aligned_plus',
  'aligned_plus': 'aligned_plus'
};

/**
 * Finds a product from the products array based on plan name and subscription mode
 * @param {Array} products - Array of product objects from API
 * @param {string} planName - Plan slug from URL params (guided, aris_plus)
 * @param {string} subscriptionMode - Subscription mode (annually/yearly, monthly)
 * @returns {Object|null} Product object with plan info or null if not found
 */
export const findPlanProduct = (products, planName, subscriptionMode) => {
  if (!products || !Array.isArray(products) || !planName || !subscriptionMode) {
    return null;
  }

  // The URL slug (e.g. `aris_plus`) may differ from the backend plan_type
  // (e.g. `aligned_plus`), so resolve it before matching.
  const targetPlanType = PLAN_TYPE_MAP[planName] || planName;

  // Try finding by product_type === 'all' and plan_type
  let product = products.find(p =>
    p.product_type === 'all' &&
    p.plan_type === targetPlanType
  );

  // Fallback: match by product name pattern
  if (!product) {
    const targetPlanPattern = PLAN_NAME_PATTERNS[planName];
    product = products.find(p =>
      p.name &&
      targetPlanPattern &&
      p.name.includes(targetPlanPattern)
    );
  }

  if (!product) {
    return null;
  }

  // Accept both `annually` and `yearly` for the yearly billing interval.
  const isYearly = subscriptionMode === 'annually' || subscriptionMode === 'yearly';
  const intervalType = isYearly ? 'yearly' : 'monthly';
  const priceInterval = product.price_plans?.intervals?.find(
    i => i.type === intervalType
  );

  if (!priceInterval) {
    return null;
  }

  return {
    product,
    priceInterval,
    displayName: PLAN_DISPLAY_NAMES[planName] || product.name,
    price: priceInterval.unit_amount / 100,
    interval: isYearly ? 'year' : 'month',
    intervalType
  };
};

/**
 * Formats plan info for display
 * @param {string} planName - Plan name from URL params
 * @param {string} subscriptionMode - Subscription mode (annually, monthly)
 * @param {number} price - Price in dollars
 * @returns {Object} Formatted plan info object
 */
export const formatPlanInfo = (planName, subscriptionMode, price) => {
  const isYearly = subscriptionMode === 'annually' || subscriptionMode === 'yearly';
  return {
    name: PLAN_DISPLAY_NAMES[planName] || planName,
    price: price,
    interval: isYearly ? 'year' : 'month'
  };
};
